"use client";

import {
  ReactForm,
  ReactFormCreate,
  ReactFormError,
  ReactFormFields,
  ReactFormUpdate,
  ReactFormDelete,
} from "@7span/react-form";
import { Button } from "ui/components/button";
import { schemaToFields, validateSchema } from "./zod-adapters";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import HeaderPortal from "../portal/header";

export function useDirtyState(values) {
  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef({});
  const timerRef = useRef(null);

  useEffect(() => {
    if (values && Object.keys(values).length > 0) {
      if (Object.keys(initialValuesRef.current).length === 0) {
        // Debounce capturing initial values to let all fields register defaults
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          initialValuesRef.current = { ...values };
          setIsDirty(false);
        }, 100);
      } else {
        const hasChanges =
          JSON.stringify(values) !== JSON.stringify(initialValuesRef.current);
        setIsDirty(hasChanges);
      }
    }

    return () => clearTimeout(timerRef.current);
  }, [values]);

  const resetDirty = () => {
    setIsDirty(false);
    initialValuesRef.current = {};
  };

  return { isDirty, resetDirty };
}

export default function ReactCrudForm({
  itemId,
  schema,
  create: createFn,
  read: readFn,
  update: updateFn,
  delete: deleteFn,
  children,
  className = "space-y-4",
  formContainerClassName = "space-y-4max-w-md",
  onSuccess = () => { },
  onCreateSuccess = () => { },
  onUpdateSuccess = () => { },
  onDeleteSuccess = () => { },
  actionPortal = "all",
  successMessage,
}) {
  const [formValues, setFormValues] = useState({});

  const { isDirty, resetDirty } = useDirtyState(formValues);

  const withSuccess =
    (fn, successHandler, defaultMessage) =>
      async (...args) => {
        try {
          const result = await fn?.(...args);
          successHandler?.(result);
          onSuccess?.(result);

          const message = successMessage || defaultMessage;
          if (message) {
            toast.success(message);
          }

          return result;
        } catch (err) {
          toast.error(err?.message || "Something went wrong");
          throw err;
        }
      };

  return (
    <ReactForm
      itemId={itemId}
      schema={schema}
      schemaToFields={schemaToFields}
      validateSchema={validateSchema}
      read={readFn}
      create={createFn && withSuccess(createFn, onCreateSuccess, "Created successfully")}
      update={
        updateFn &&
        withSuccess(updateFn, () => {
          resetDirty();
          onUpdateSuccess();
        }, "Updated successfully")
      }
      delete={deleteFn && withSuccess(deleteFn, onDeleteSuccess, "Deleted successfully")}
    >
      {({ context }) => {
        // Update form values when context changes

        if (JSON.stringify(context.values) !== JSON.stringify(formValues)) {
          setFormValues(context.values || {});
        }

        return (
          <div className={className}>
            <ReactFormError />

            <ReactFormFields className={formContainerClassName}>
              {typeof children === "function"
                ? (fieldsProps) =>
                  children({
                    context,
                    dirtyValues: isDirty ? context.values : {},
                    formValues,
                    ...fieldsProps,
                  })
                : children}
            </ReactFormFields>

            {actionPortal !== "none" && (
              <div className="flex gap-2">
                <HeaderPortal>
                  <ReactFormCreate>
                    {({ createItem, isCreating, isLoading }) => (
                      <Button
                        onClick={createItem}
                        disabled={isLoading || !isDirty}
                      >
                        {/* {isLoading && (
                          <Loader2 className="size-4 animate-spin mr-2" />
                        )} */}
                        {isCreating ? "Creating..." : "Create"}
                      </Button>
                    )}
                  </ReactFormCreate>
                </HeaderPortal>

                <HeaderPortal>
                  <ReactFormCreate>
                    {({ createItem, isCreating, isLoading }) => (
                      <Button onClick={createItem} disabled={isLoading}>
                        {/* {isLoading && (
                          <Loader2 className="size-4 animate-spin mr-2" />
                        )} */}

                        {isCreating ? "Creating..." : "Create"}
                      </Button>
                    )}
                  </ReactFormCreate>
                </HeaderPortal>

                <HeaderPortal>
                  <ReactFormUpdate>
                    {({ updateItem, isUpdating, isLoading }) => (
                      <Button
                        onClick={updateItem}
                        disabled={isLoading || !isDirty}
                      >
                        {/* {isLoading && (
                          <Loader2 className="size-4 animate-spin mr-2" />
                        )} */}

                        {isUpdating ? "Updating..." : "Update"}
                      </Button>
                    )}
                  </ReactFormUpdate>
                </HeaderPortal>

                {deleteFn ? (
                  <HeaderPortal>
                    <ReactFormDelete>
                      {({ deleteItem, isDeleting, isLoading }) => (
                        <Button
                          variant="destructive"
                          onClick={deleteItem}
                          disabled={isLoading}
                        >
                          {/* {isLoading && (
                              <Loader2 className="size-4 animate-spin mr-2" />
                            )} */}
                          {isDeleting ? "Deleting..." : "Delete"}
                        </Button>
                      )}
                    </ReactFormDelete>
                  </HeaderPortal>
                ) : null}
              </div>
            )}
          </div>
        );
      }}
    </ReactForm>
  );
}

export * from "./textarea";
